import { Image, Spin } from 'antd'
import { useGeckoLocale } from 'features/locale'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useMemo } from 'react'
import { GeckoMeasurementSystem } from 'types'

/**
 * Returns the rendered visualization file path based on the unit system.
 * Files are stored on the indication entry at:
 * - graphs/rendered-visualization-metric.png (mm units)
 * - graphs/rendered-visualization-imperial.png (in units)
 */
export function getRenderedVisualizationPath(isMetric: boolean): string {
  return isMetric
    ? 'graphs/rendered-visualization-metric.png'
    : 'graphs/rendered-visualization-imperial.png'
}

type RenderedVisualizationProps =
  | {
      /** Indication entry ID to fetch the visualization from when imageUrl is not provided */
      indicationId: string
      /** Optional pre-fetched image URL. When provided, skips fetching from Rolodex. */
      imageUrl?: string
    }
  | {
      indicationId?: undefined
      /** Pre-fetched image URL to display directly */
      imageUrl: string
    }

/**
 * Component to display the rendered visualization for an indication.
 *
 * If imageUrl is provided, it will be used directly.
 * Otherwise, fetches from the indication entry based on the current unit system.
 */
export const RenderedVisualization: React.FC<RenderedVisualizationProps> = ({
  indicationId,
  imageUrl: providedImageUrl,
}) => {
  const { measurementSystem } = useGeckoLocale()
  const isMetric = measurementSystem === GeckoMeasurementSystem.Metric

  // Get the rendered visualization path based on unit system
  const renderedVisualizationPath = useMemo(
    () => getRenderedVisualizationPath(isMetric),
    [isMetric]
  )

  // Only fetch from Rolodex if no URL was provided and we have an indication ID
  const shouldFetch = !providedImageUrl && !!indicationId
  const { results: renderedImageUrlResults, isLoading } = useRolodexSignedURLs(
    shouldFetch
      ? [
          {
            entry_id: indicationId,
            path: renderedVisualizationPath,
          },
        ]
      : []
  )

  const fetchedImageUrl = renderedImageUrlResults[0]?.signed_url
  const renderedImageUrl = providedImageUrl || fetchedImageUrl

  if (isLoading && shouldFetch) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', marginBottom: 16 }}>
        <Spin tip='Loading visualization...' />
      </div>
    )
  }

  if (!renderedImageUrl) {
    return null
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Image
        src={renderedImageUrl}
        alt='Rendered visualization'
        style={{ width: '100%' }}
      />
    </div>
  )
}
